import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Flame, AlertTriangle, Check, Shield, Users } from 'lucide-react';

const DRILL_KEY = 'evoke-fire-drill-session';

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const formatDateLocal = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * FireDrill
 *
 * Three states:
 *   idle    → show "Start Fire Drill" button + building occupancy summary
 *   active  → checklist of everyone in building; mark each as accounted for
 *   ended   → summary of results; option to start a new drill
 *
 * Drill state is persisted to localStorage so a page reload during an active
 * drill doesn't lose progress. Results are passed to onDrillEnd which saves
 * them to the FireDrills SharePoint list.
 */
export default function FireDrill({ attendanceRecords = [], today, onDrillStart, onDrillEnd }) {
  const [phase, setPhase] = useState('idle'); // 'idle' | 'active' | 'ended'
  const [drillStartTime, setDrillStartTime] = useState(null);
  const [accountedFor, setAccountedFor] = useState(new Set()); // Set<personId>
  const [drillResult, setDrillResult] = useState(null);

  // People currently in building at drill time (snapshot taken on start)
  const [drillSnapshot, setDrillSnapshot] = useState([]);

  // Resume an in-progress drill after page reload
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRILL_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.date === today && !saved.endTime) {
        setPhase('active');
        setDrillStartTime(saved.startTime);
        setAccountedFor(new Set(saved.accountedFor || []));
        setDrillSnapshot(saved.snapshot || []);
      }
    } catch { /* ignore */ }
  }, [today]);

  // Current occupancy (live, from parent's state)
  const currentlyIn = useMemo(
    () => attendanceRecords.filter(r => r.isCheckedIn),
    [attendanceRecords]
  );

  const occupancy = useMemo(() => ({
    total:    currentlyIn.length,
    staff:    currentlyIn.filter(r => r.personType === 'staff').length,
    clients:  currentlyIn.filter(r => r.personType === 'client').length,
    visitors: currentlyIn.filter(r => !['staff', 'client'].includes(r.personType)).length,
  }), [currentlyIn]);

  // ── Start drill ─────────────────────────────────────────────────────────────

  const startDrill = useCallback(() => {
    const startTime = new Date().toISOString();
    const snapshot = currentlyIn.map(r => ({
      personId:   r.personId,
      personName: r.personName,
      personType: r.personType,
      role:        r.role || '',
      checkInTime: r.checkInTime,
    }));

    setDrillStartTime(startTime);
    setAccountedFor(new Set());
    setDrillSnapshot(snapshot);
    setPhase('active');

    localStorage.setItem(DRILL_KEY, JSON.stringify({
      date: today, startTime, snapshot, accountedFor: [], endTime: null,
    }));
    onDrillStart?.();
  }, [currentlyIn, today, onDrillStart]);

  // ── Toggle accounted for ────────────────────────────────────────────────────

  const toggleAccounted = useCallback((personId) => {
    setAccountedFor(prev => {
      const next = new Set(prev);
      next.has(personId) ? next.delete(personId) : next.add(personId);
      try {
        const raw = JSON.parse(localStorage.getItem(DRILL_KEY) || '{}');
        raw.accountedFor = Array.from(next);
        localStorage.setItem(DRILL_KEY, JSON.stringify(raw));
      } catch { /* ignore */ }
      return next;
    });
  }, []);

  // ── End drill ───────────────────────────────────────────────────────────────

  const endDrill = useCallback(() => {
    const endTime = new Date().toISOString();
    const unaccountedNames = drillSnapshot
      .filter(p => !accountedFor.has(p.personId))
      .map(p => p.personName);

    const result = {
      date: today,
      startTime: drillStartTime,
      endTime,
      totalCount: drillSnapshot.length,
      accountedCount: accountedFor.size,
      unaccountedNames,
    };

    try {
      const raw = JSON.parse(localStorage.getItem(DRILL_KEY) || '{}');
      raw.endTime = endTime;
      localStorage.setItem(DRILL_KEY, JSON.stringify(raw));
    } catch { /* ignore */ }

    setDrillResult(result);
    setPhase('ended');
    onDrillEnd?.(result);
  }, [drillSnapshot, accountedFor, drillStartTime, today, onDrillEnd]);

  // ── Reset ────────────────────────────────────────────────────────────────────

  const resetDrill = () => {
    localStorage.removeItem(DRILL_KEY);
    setPhase('idle');
    setDrillStartTime(null);
    setAccountedFor(new Set());
    setDrillSnapshot([]);
    setDrillResult(null);
  };

  // ═══ Idle ════════════════════════════════════════════════════════════════════

  if (phase === 'idle') {
    return (
      <div className="text-center py-16 space-y-8 max-w-lg mx-auto">
        <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
          <Flame className="w-12 h-12 text-orange-500" />
        </div>

        <div>
          <h2 className="text-2xl font-bold text-gray-800">Fire Drill Mode</h2>
          <p className="text-gray-500 mt-2 max-w-sm mx-auto text-sm">
            Starting a fire drill takes a snapshot of everyone currently checked in and gives
            you a checklist to account for each person during an evacuation.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 max-w-xs mx-auto">
          <p className="text-blue-800 font-bold text-2xl">{occupancy.total}</p>
          <p className="text-blue-700 text-sm font-medium mt-0.5">People currently in building</p>
          <p className="text-blue-500 text-xs mt-2">
            {occupancy.staff} staff · {occupancy.clients} clients · {occupancy.visitors} visitors
          </p>
        </div>

        {occupancy.total === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-sm mx-auto text-sm text-amber-700">
            No one is currently checked in. Use the Building View tab to check people in before running a drill.
          </div>
        )}

        <button
          onClick={startDrill}
          className="px-10 py-4 bg-red-600 text-white rounded-xl font-bold text-xl hover:bg-red-700 shadow-lg hover:shadow-xl transition-all active:scale-95"
        >
          Start Fire Drill
        </button>
      </div>
    );
  }

  // ═══ Active drill ════════════════════════════════════════════════════════════

  if (phase === 'active') {
    const total = drillSnapshot.length;
    const counted = accountedFor.size;
    const allSafe = counted >= total && total > 0;
    const notAccounted = drillSnapshot.filter(p => !accountedFor.has(p.personId));

    return (
      <div className="space-y-5">
        {/* Header */}
        <div className={`rounded-xl p-6 text-white ${allSafe ? 'bg-green-600' : 'bg-red-600'}`}>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Flame className="w-8 h-8 flex-shrink-0" />
              <div>
                <div className="text-xl font-bold tracking-wide">FIRE DRILL IN PROGRESS</div>
                <div className="text-sm opacity-80">
                  Started at {fmt(drillStartTime)} · {new Date().toLocaleDateString('en-US', {
                    weekday: 'long', month: 'long', day: 'numeric',
                  })}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold leading-none">{counted}/{total}</div>
              <div className="text-sm opacity-80 mt-1">Accounted For</div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-5 bg-white/30 rounded-full h-4">
            <div
              className="bg-white rounded-full h-4 transition-all duration-500"
              style={{ width: total > 0 ? `${Math.round((counted / total) * 100)}%` : '0%' }}
            />
          </div>

          {allSafe && (
            <div className="mt-4 flex items-center gap-2 text-lg font-bold">
              <Check className="w-6 h-6" /> ALL PERSONS ACCOUNTED FOR
            </div>
          )}
        </div>

        {/* Not accounted for (shown first) */}
        {notAccounted.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-red-100 border-b border-red-200 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-700" />
              <span className="font-bold text-red-800">Not Yet Accounted For ({notAccounted.length})</span>
            </div>
            <div className="divide-y divide-red-100">
              {notAccounted.map(person => (
                <div key={person.personId} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <div className="font-semibold text-gray-800">{person.personName}</div>
                    <div className="text-xs text-gray-400 capitalize mt-0.5">
                      {person.personType}
                      {person.role ? ` · ${person.role}` : ''}
                      {' · '}In since {fmt(person.checkInTime)}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleAccounted(person.personId)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold text-sm hover:bg-green-700 transition-colors whitespace-nowrap"
                  >
                    Mark Safe ✓
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full roster */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <span className="font-semibold text-gray-700">All People in Building ({total})</span>
          </div>
          <div className="divide-y divide-gray-100">
            {drillSnapshot.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">
                No one was checked in when the drill started.
              </div>
            ) : (
              drillSnapshot.map(person => {
                const safe = accountedFor.has(person.personId);
                return (
                  <div
                    key={person.personId}
                    className={`flex items-center justify-between px-4 py-3.5 transition-colors ${safe ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        onClick={() => toggleAccounted(person.personId)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer flex-shrink-0 transition-colors ${
                          safe ? 'bg-green-500 border-green-500' : 'border-gray-300 hover:border-green-400'
                        }`}
                      >
                        {safe && <Check className="w-3.5 h-3.5 text-white" />}
                      </div>
                      <div>
                        <div className={`font-semibold ${safe ? 'text-green-800' : 'text-gray-800'}`}>
                          {person.personName}
                        </div>
                        <div className="text-xs text-gray-400 capitalize">
                          {person.personType}
                          {person.role ? ` · ${person.role}` : ''}
                          {' · '}In since {fmt(person.checkInTime)}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleAccounted(person.personId)}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap ${
                        safe
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {safe ? '✓ Accounted For' : 'Mark Safe'}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <button
          onClick={endDrill}
          className="w-full py-4 bg-gray-700 text-white rounded-xl font-bold text-lg hover:bg-gray-800 transition-colors"
        >
          End Fire Drill — Record Results
        </button>
      </div>
    );
  }

  // ═══ Ended ════════════════════════════════════════════════════════════════════

  if (phase === 'ended' && drillResult) {
    const allAccountedFor = drillResult.accountedCount === drillResult.totalCount;
    const duration = drillResult.endTime && drillResult.startTime
      ? Math.round((new Date(drillResult.endTime) - new Date(drillResult.startTime)) / 1000 / 60)
      : null;

    return (
      <div className="max-w-lg mx-auto text-center py-12 space-y-8">
        <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto ${
          allAccountedFor ? 'bg-green-100' : 'bg-yellow-100'
        }`}>
          {allAccountedFor
            ? <Shield className="w-12 h-12 text-green-600" />
            : <AlertTriangle className="w-12 h-12 text-yellow-600" />}
        </div>

        <div>
          <h2 className="text-2xl font-bold text-gray-800">Fire Drill Complete</h2>
          <p className="text-gray-500 mt-1 text-sm">
            {fmt(drillResult.startTime)} → {fmt(drillResult.endTime)}
            {duration !== null && ` · ${duration} minute${duration !== 1 ? 's' : ''}`}
          </p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-3">
          <div className="text-5xl font-bold text-gray-800">
            {drillResult.accountedCount}/{drillResult.totalCount}
          </div>
          <div className="text-gray-500 text-sm">People Accounted For</div>

          {allAccountedFor ? (
            <div className="flex items-center justify-center gap-2 text-green-600 font-semibold">
              <Check className="w-5 h-5" /> All persons accounted for
            </div>
          ) : (
            <div className="text-left mt-4">
              <p className="text-red-600 font-semibold text-sm mb-2">
                {drillResult.unaccountedNames.length} person(s) not accounted for:
              </p>
              <ul className="space-y-1">
                {drillResult.unaccountedNames.map(name => (
                  <li key={name} className="text-sm text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400">
          Results have been saved to SharePoint (FireDrills list).
        </p>

        <button
          onClick={resetDrill}
          className="px-8 py-3 bg-gray-700 text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors"
        >
          Start New Drill
        </button>
      </div>
    );
  }

  return null;
}
